import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { getUser } from 'constants/apiUrls';
import { routes } from 'constants/routes';
import { Loader } from 'components/Common/Loader';
import { useAuth } from 'hooks/useAuth';
import { http } from 'middleware/apiMiddleware';

interface DecodedToken {
  user_id: string;
  exp: number;
  iat: number;
}

const VerifyAuth = () => {
  const { token, businessId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authContext = useAuth();
  const [error, setError] = useState<string | null>(null);

  const getRedirectUrl = () => {
    // Handle onboarding-business redirect from mobile app
    if (businessId === 'onboarding-business') {
      const queryString = searchParams.toString();
      return queryString
        ? `${routes.onboardingBusiness}?${queryString}`
        : routes.onboardingBusiness;
    }

    // Default: redirect to choose-business with businessId
    return `${routes.chooseBusiness}/${businessId}?${searchParams.toString()}`;
  };

  const fetchUserDetails = async () => {
    try {
      // Validate token exists
      if (!token) {
        setError('No token provided');
        navigate(routes.login);
        return;
      }

      // Decode token
      let decoded: DecodedToken;
      try {
        decoded = jwtDecode<DecodedToken>(token);
      } catch {
        setError('Invalid token');
        navigate(routes.login);
        return;
      }

      // Check if token is expired
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        setError('Token expired');
        navigate(routes.login);
        return;
      }

      localStorage.setItem('token', JSON.stringify(token));
      const { data, error: apiError } = await http.get(getUser(decoded.user_id));

      if (apiError) {
        setError('Failed to fetch user details');
        navigate(routes.login);
        return;
      }

      const payload = { data: { access: token, ...data } };
      const redirectUrl = getRedirectUrl();
      authContext?.login(payload, redirectUrl);
    } catch {
      setError('An error occurred');
      navigate(routes.login);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <p className="text-red-500 mb-4">{error}</p>
        <p className="text-darkBlack">Redirecting to login...</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-screen">
      <Loader color="darkBlue" />
    </div>
  );
};

export default VerifyAuth;
